import {
  action,
  computed,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from 'mobx';
import { ConditionalModalState } from './ConditionalModalState';
import { CustomFieldCollectionOptionsState } from './CustomFieldCollectionOptionsState';
import { CustomFieldModalBiReporter } from './CustomFieldModalBiReporter';
import { FieldState, FormState } from 'formstate';
import { ToggleState } from './ToggleState';
import { CustomFieldOrigin, CustomFieldType, ReportBI } from '../services';
import {
  FieldDraft,
  FieldManagement,
  FieldModalCollectionInput,
  FieldModalCollectionOption,
  FieldModalCollectionOptionsLoader,
  LockableFieldProperty,
  ResolvedFieldTypeOption,
} from '../types/FieldsSource';
import type { FieldSettings } from '../types/Schema';
import { WixPatternsContainer } from './WixPatternsContainer';
import {
  isHttpError,
  isReferenceField,
  isReferenceFieldType,
  slugify,
} from '../util';
import { isDetailsResponse, isErrorDetails } from '../util/isHttpError';
import { ToastConfig } from '../model';

/** The modal's name input caps entry at this length; `applyInitialField`
 *  applies the same cap so a prefilled name can't exceed what a user could
 *  have typed. */
export const CUSTOM_FIELD_NAME_MAX_LENGTH = 64;

export interface CustomFieldStateParams {
  fieldManagement: FieldManagement;
  container: WixPatternsContainer;
  reportBi: ReportBI;
  /** Field count for the "numAdded" BI param. */
  fieldCount: () => number;
  /**
   * Pure mode: when provided, confirm validates and hands the collected draft to
   * `onSubmit` instead of creating/editing the field through `fieldManagement`
   * (no server side effect, no success toast). Used by the CSV import flow, which
   * defers field creation to a single batch step before upload. `fieldManagement`
   * is still required — the form reads its type options/permissions/keys.
   */
  onSubmit?: (draft: FieldDraft) => void;
}

export interface CustomFieldForm {
  type?: CustomFieldType;
  key: string;
  name: string;
  referencedCollectionId?: string;
  readApps: boolean;
  readUsers: boolean;
  writeApps: boolean;
  writeUsers: boolean;
  containsPii: boolean;
}

export interface CustomFieldFormFields {
  [key: string]: FieldState<any>;
  type: FieldState<string | undefined>;
  key: FieldState<string>;
  name: FieldState<string>;
  readApps: FieldState<boolean>;
  readUsers: FieldState<boolean>;
  writeApps: FieldState<boolean>;
  writeUsers: FieldState<boolean>;
  containsPii: FieldState<boolean>;
  referencedCollectionId: FieldState<string | undefined>;
}

export class CustomFieldModalState {
  fieldManagement: FieldManagement;
  container: WixPatternsContainer;
  fieldCount: () => number;
  customFieldModalBiReporter: CustomFieldModalBiReporter;
  settings = new ToggleState();
  errorToastConfig: ToastConfig | undefined = undefined;
  mode: 'new' | 'edit' = 'new';
  origin: CustomFieldOrigin = 'Custom columns panel';
  customOrigin?: string;
  lockedProperties: readonly LockableFieldProperty[] = [];
  onSave?: (fieldId: string) => void;
  reportBi: ReportBI;
  private readonly onSubmit?: (draft: FieldDraft) => void;
  private readonly collectionOptionsState =
    new CustomFieldCollectionOptionsState();
  /** The `required` value requested by `applyInitialField`, reapplied whenever
   *  the picked type changes — a type picked after the fact (or an unlocked
   *  type the user later changes) would otherwise start the settings section
   *  fresh and silently drop it. */
  private pendingRequired?: boolean;

  modalState = new ConditionalModalState({
    onConfirm: async () => {
      if (this.mode === 'new' && this.fieldManagement.maxReached) {
        return;
      }
      const {
        form,
        mode,
        customFieldModalBiReporter: { reportModalEndProcess },
        t,
      } = this;

      const validation = await form.validate();

      if (validation.hasError) {
        this.enableAutoValidation();
        throw new Error('validation');
      }

      if (this.settingsHasError) {
        this.settings.setOn();
        throw new Error('validation');
      }

      try {
        const draft = this.getFormValues();

        // Pure mode (CSV import): hand the draft back; no server mutation, no
        // success toast (the field isn't created here — it's batched pre-upload).
        if (this.onSubmit) {
          this.onSubmit(draft);
          reportModalEndProcess({ endType: 'Add Field' });
          return;
        }

        // Captured before the `await`: if the modal gets reopened with a
        // different callback while this save is in flight, the result still
        // reaches the callback that was active when the user confirmed.
        const onSave = this.onSave;
        // The field is already saved and merged by the time this runs, so a
        // throwing consumer callback must not look like a failed submission
        // (which would keep the modal open for a retry that creates a
        // duplicate).
        const notifySaved = (fieldId: string) => {
          try {
            onSave?.(fieldId);
          } catch (callbackError: unknown) {
            this.container.errorMonitor.captureException(callbackError);
          }
        };

        if (mode === 'new') {
          const createdKey = await this.fieldManagement.addField(draft);
          if (createdKey) {
            notifySaved(createdKey);
          }
        } else {
          await this.fieldManagement.editField(draft.key, draft);
          notifySaved(draft.key);
        }

        this.container.showToast?.({
          message:
            mode === 'new'
              ? t('cairo.customFields.successToast')
              : t('cairo.customFields.editCustomField.success.toast'),
          type: 'SUCCESS',
          biName: 'cairo-custom-field-success-toast',
        });

        reportModalEndProcess({ endType: 'Add Field' });
      } catch (e: unknown) {
        this.onConfirmError(e);
      }
    },
  });

  form: FormState<CustomFieldFormFields> = new FormState({
    type: new FieldState<string | undefined>(undefined).validators((value) => {
      if (!value) {
        return this.t(
          'cairo.customFields.addCustomField.modal.emptyFieldType.error',
        );
      }

      return undefined;
    }),
    name: new FieldState('').validators((value) => {
      if (!value) {
        return this.t(
          'cairo.customFields.addCustomField.modal.emptyFieldName.error',
        );
      }

      return undefined;
    }),
    key: new FieldState('').validators((value) => {
      const { t, mode } = this;

      if (!value) {
        return this.t(
          'cairo.customFields.addCustomField.modal.emptyFieldName.error',
        );
      }

      if (mode === 'new' && this.containsKey(value)) {
        return t('cairo.customFields.addCustomField.modal.sameKey.error');
      }

      if (!/^[a-zA-Z][a-zA-Z0-9_]*$/g.test(value)) {
        return t('cairo.customFields.addCustomField.modal.keyFormat.error');
      }

      return undefined;
    }),
    readUsers: new FieldState(false),
    readApps: new FieldState(false),
    writeUsers: new FieldState(false),
    writeApps: new FieldState(false),
    containsPii: new FieldState(false),
    referencedCollectionId: new FieldState<string | undefined>(
      undefined,
    ).validators((value) => {
      const collectionInput = this.collectionInput;

      if (
        this.mode === 'new' &&
        collectionInput &&
        (collectionInput.required ?? true) &&
        !value
      ) {
        return (
          collectionInput.errorMessage ??
          collectionInput.label ??
          this.selectedFieldType?.label
        );
      }

      return undefined;
    }),
  });

  disabledFields = {
    permissions: {
      read: { apps: true, users: true },
      write: { apps: true, users: true },
    },
  };

  constructor(params: CustomFieldStateParams) {
    this.fieldManagement = params.fieldManagement;
    this.container = params.container;
    this.fieldCount = params.fieldCount;
    this.reportBi = params.reportBi;
    this.onSubmit = params.onSubmit;
    this.customFieldModalBiReporter = new CustomFieldModalBiReporter({
      customFieldModalState: this,
    });

    makeObservable(this, {
      selectedFieldType: computed,
      namePlaceholder: computed,
      typeSupportsPii: computed,
      collectionInput: computed,
      collectionOptionsLoader: computed,
      collectionInputLabel: computed,
      collectionInputPlaceholder: computed,
      collectionInputEmptyStateMessage: computed,
      collectionOptions: computed,
      collectionOptionsLoading: computed,
      collectionOptionsError: computed,
      showSettings: computed,
      settingsHasError: computed,
      isSubmitting: computed,
      disabledFields: observable.ref,
      errorToastConfig: observable.ref,
      mode: observable.ref,
      lockedProperties: observable.ref,
      applyInitialField: action,
      reset: action,
      resetSettingsValues: action,
      resetCollectionInput: action,
      setReferencedCollectionId: action.bound,
      loadCollectionOptions: action.bound,
      setFormValues: action,
      generateUniqueKey: action.bound,
      setKeyFieldIfNotDirty: action.bound,
      setDefaultPermissions: action,
    });

    // Reapplies a pending `required` prefill whenever the picked type changes
    // — covers both picking a type after `applyInitialField` requested it,
    // and the settings section resetting when the user later changes an
    // unlocked type. Lives for this state's lifetime; never disposed, like
    // the object itself.
    reaction(
      () => this.form.$.type.value,
      () => this.reapplyPendingRequired(),
    );
  }

  private reapplyPendingRequired() {
    if (this.pendingRequired === undefined) {
      return;
    }
    if (this.settingsSection?.setInitialValues) {
      this.settingsSection.setInitialValues({
        required: this.pendingRequired,
      });
      return;
    }
    if (this.settingsSection) {
      // The picked type's section has no way to apply the prefill, so
      // locking 'required' would only disable the checkbox at its unset
      // default with no way to ever satisfy the requested value — drop the
      // lock rather than present it as enforced.
      runInAction(() => {
        this.lockedProperties = this.lockedProperties.filter(
          (property) => property !== 'required',
        );
      });
    }
  }

  onConfirmError(e: unknown) {
    const { getResolvedError } = this.container.errorHandler;

    runInAction(() => {
      const resolvedError = getResolvedError?.(e);
      const httpError = isHttpError(e) ? e : undefined;

      if (
        isDetailsResponse(httpError?.response?.data) &&
        isErrorDetails(httpError?.response?.data.details) &&
        httpError?.response?.data?.details?.validationError?.fieldViolations?.some(
          (v) => v.ruleName === 'EXCEEDED_STORED_DATA_SIZE',
        )
      ) {
        this.errorToastConfig = {
          message: this.t('cairo.customFields.modal.maxFieldsError'),
          action: {
            text: this.t('cairo.error.contactSupport.cta'),
            onClick: () => {
              window.open('https://www.wix.com/contact', '_blank');
            },
          },
        };
      } else {
        this.errorToastConfig = {
          message: resolvedError?.message!,
          action: resolvedError?.action,
          details: resolvedError?.requestId
            ? {
                requestId: resolvedError.requestId,
              }
            : undefined,
        };
      }
    });

    this.customFieldModalBiReporter.reportSubmitFailure({
      errorType: this.errorToastConfig?.message,
    });

    // Prevents modal closing
    throw e;
  }
  reset() {
    const {
      form,
      settings,
      disabledFields: { permissions },
    } = this;

    form.reset();
    settings.setOff();
    this.resetSettingsValues();
    this.resetCollectionInput();
    this.errorToastConfig = undefined;
    this.disableAutoValidation();
    // `applyInitialField` runs only for a new-field open; clear it here too
    // so a stale value can't leak from a previous open into an edit-mode one.
    this.pendingRequired = undefined;

    permissions.read.apps = false;
    permissions.read.users = false;
    permissions.write.apps = false;
    permissions.write.users = false;
  }

  /** Clears the settings sections back to their defaults. Goes over every type
   *  option rather than the picked one: `reset()` runs after the form is
   *  cleared, when no type is picked. */
  resetSettingsValues() {
    this.fieldManagement.fieldModal.types.forEach((type) =>
      type.settingsSection?.setValues(),
    );
  }

  enableAutoValidation() {
    const {
      form: {
        $: { key, name, type },
      },
    } = this;

    key.enableAutoValidation();
    name.enableAutoValidation();
    type.enableAutoValidation();
    this.form.$.referencedCollectionId.enableAutoValidation();
  }

  disableAutoValidation() {
    const {
      form: {
        $: { key, name, type },
      },
    } = this;

    key.disableAutoValidation();
    name.disableAutoValidation();
    type.disableAutoValidation();
    this.form.$.referencedCollectionId.disableAutoValidation();
  }

  getFormValues(): FieldDraft {
    const {
      form: {
        $: {
          type,
          key,
          name,
          readApps,
          readUsers,
          writeApps,
          writeUsers,
          containsPii,
          referencedCollectionId,
        },
      },
    } = this;
    const sectionSettings = this.settingsSection?.getValues(type.value);
    const collectionSettings =
      referencedCollectionId.value && this.collectionInput?.getSettings
        ? this.collectionInput.getSettings(referencedCollectionId.value)
        : undefined;

    return {
      type: type.value,
      key: key.value,
      name: name.value,
      referencedCollectionId: referencedCollectionId.value,
      readApps: readApps.value,
      readUsers: readUsers.value,
      writeApps: writeApps.value,
      writeUsers: writeUsers.value,
      containsPii: containsPii.value,
      settings: combineFieldSettings(sectionSettings, collectionSettings),
    };
  }

  setFormValues(
    form: FieldDraft,
    params?: { disablePermissionCheckboxes: boolean },
  ) {
    const {
      form: { $ },
    } = this;

    $.key.value = form.key;
    $.name.value = form.name;
    $.type.value = form.type;
    $.readApps.value = form.readApps;
    $.readUsers.value = form.readUsers;
    $.writeApps.value = form.writeApps;
    $.writeUsers.value = form.writeUsers;
    $.containsPii.value = form.containsPii;
    $.referencedCollectionId.value =
      form.referencedCollectionId ??
      (form.field ? this.collectionInput?.getValue?.(form.field) : undefined) ??
      (isReferenceField(form.field)
        ? form.field.referenceMetadata.referencedCollectionId
        : undefined);
    // After `$.type.value` above, so it lands on the picked type's section.
    this.settingsSection?.setValues(form.field);
    if (params?.disablePermissionCheckboxes) {
      if (form.readUsers) {
        this.disabledFields.permissions.read.users = true;
      }

      if (form.readApps) {
        this.disabledFields.permissions.read.apps = true;
      }

      if (form.writeUsers) {
        this.disabledFields.permissions.write.users = true;
      }

      if (form.writeApps) {
        this.disabledFields.permissions.write.apps = true;
      }
    }
  }

  /** Prefills a new field from the caller's requested type/name/required. Sets
   *  values directly (like `setFormValues`) rather than through `onChange`, so
   *  the name field isn't marked dirty by the prefill — a later user edit
   *  still regenerates the key. Also trims `lockedProperties` down to
   *  properties actually prefilled here — locking a property with nothing to
   *  lock it to would leave it stuck on its empty default. */
  applyInitialField(initialField?: {
    type?: string;
    name?: string;
    required?: boolean;
  }) {
    if (!initialField) {
      this.lockedProperties = [];
      this.pendingRequired = undefined;
      return;
    }
    const {
      form: { $ },
    } = this;
    const provided: LockableFieldProperty[] = [];

    // Set before `type` below: reassigning `type.value` reapplies it via the
    // type-change reaction once the action this method runs in completes, so
    // it's already in place whether or not a type is picked here.
    if (initialField.required !== undefined) {
      this.pendingRequired = initialField.required;
      provided.push('required');
    }

    // Ignores a type the source doesn't offer, rather than locking the picker
    // on a value it can never show as selected.
    if (
      initialField.type !== undefined &&
      this.fieldManagement.fieldModal.types.some(
        (type) => type.id === initialField.type,
      )
    ) {
      $.type.value = initialField.type;
      provided.push('type');
    } else {
      // No type picked here (or it wasn't valid), so the type-change
      // reaction won't fire — apply directly in case a type is already set
      // from an earlier open, or a settings section doesn't need one.
      this.reapplyPendingRequired();
    }
    if (initialField.name !== undefined) {
      // Independent of `type` — a caller may want to suggest a name without
      // constraining which type the user picks.
      $.name.value = initialField.name.slice(0, CUSTOM_FIELD_NAME_MAX_LENGTH);
      provided.push('name');
      this.setKeyFieldIfNotDirty(this.generateUniqueKey($.name.value));
    }

    this.lockedProperties = this.lockedProperties.filter((property) =>
      provided.includes(property),
    );
  }

  setDefaultPermissions() {
    const {
      form: { $ },
    } = this;
    const permissions = this.fieldManagement.fieldModal.permissions;
    if (!permissions?.installedApps?.hide) {
      $.readApps.value = !!permissions?.installedApps?.defaultRead;
      $.writeApps.value = !!permissions?.installedApps?.defaultWrite;
    }

    if (!permissions?.siteVisitors?.hide) {
      $.readUsers.value = !!permissions?.siteVisitors?.defaultRead;
      $.writeUsers.value = !!permissions?.siteVisitors?.defaultWrite;
    }
  }

  setKeyFieldIfNotDirty = (value: string) => {
    const {
      form: {
        $: { key },
      },
      mode,
    } = this;

    if (mode === 'new' && !key.dirty) {
      // In the formstate library setting a field value using
      // the onChange API sets the dirty state of the field to true.
      // However, since here it's not a manual user input
      // we avoid changing the dirty state by setting a value directly.
      key.value = value;
    }
  };

  resetCollectionInput() {
    this.form.$.referencedCollectionId.value = undefined;
    this.form.$.referencedCollectionId.disableAutoValidation();
    this.collectionOptionsState.clearError();
  }

  setReferencedCollectionId(collectionId: string | undefined) {
    this.form.$.referencedCollectionId.onChange(collectionId);
  }

  async loadCollectionOptions() {
    await this.collectionOptionsState.load(this.collectionOptionsLoader);
  }

  private containsKey(key: string) {
    return this.fieldManagement.existingFieldKeys.includes(key);
  }

  generateUniqueKey(name: string) {
    const slug = slugify(name);
    const type = this.form.$.type.value;

    if (slug && slug.length >= 4 && !this.containsKey(slug)) {
      return slug;
    }

    if (!type) {
      return '';
    }

    let index = 1;

    while (this.containsKey(`${type}_${index}`)) {
      index += 1;
    }

    return `${type}_${index}`;
  }

  get t() {
    return this.container.translate;
  }

  get isSubmitting() {
    return this.modalState.modal.confirming.isLoading;
  }

  get selectedFieldType(): ResolvedFieldTypeOption | undefined {
    return this.fieldManagement.fieldModal.types.find(
      (option) => option.id === this.form.$.type.value,
    );
  }

  get collectionInput(): FieldModalCollectionInput | undefined {
    if (!this.selectedFieldType) {
      return undefined;
    }

    const explicitInput = this.selectedFieldType.collectionInput;
    const autoWired =
      isReferenceFieldType(this.selectedFieldType.patternsType) &&
      this.fieldManagement.fieldModal.loadReferenceableCollectionOptions;

    if (!explicitInput && !autoWired) {
      return undefined;
    }

    return explicitInput ?? {};
  }

  get collectionOptionsLoader(): FieldModalCollectionOptionsLoader | undefined {
    const collectionInput = this.collectionInput;

    if (!collectionInput || !this.selectedFieldType) {
      return undefined;
    }

    return (
      collectionInput.loadOptions ??
      (isReferenceFieldType(this.selectedFieldType.patternsType)
        ? this.fieldManagement.fieldModal.loadReferenceableCollectionOptions
        : undefined)
    );
  }

  get collectionInputLabel() {
    return this.collectionInput?.label ?? this.selectedFieldType?.label ?? '';
  }

  get collectionInputPlaceholder() {
    return this.collectionInput?.placeholder ?? this.collectionInputLabel;
  }

  get collectionInputEmptyStateMessage() {
    return (
      this.collectionInput?.emptyStateMessage ??
      this.t('cairo.emptyState.subtitle.nothingMatchesCriteria')
    );
  }

  get collectionOptions(): FieldModalCollectionOption[] {
    return this.collectionOptionsState.options;
  }

  get collectionOptionsLoading() {
    return this.collectionOptionsState.loading;
  }

  get collectionOptionsError() {
    return this.collectionOptionsState.error;
  }

  get namePlaceholder() {
    return (
      this.selectedFieldType?.namePlaceholder ??
      this.selectedFieldType?.label ??
      this.t('cairo.customFields.addCustomField.modal.fieldName.placeholder')
    );
  }

  /** Whether the PII toggle shows: the picked type decides, and a type with no
   *  opinion follows the source. Checked on the raw value, not on
   *  `selectedFieldType` — editing a field whose type the source can't create
   *  finds no option, and the toggle still belongs there. */
  get typeSupportsPii() {
    if (this.form.$.type.value == null) {
      return false;
    }
    return (
      this.selectedFieldType?.supportsPii ??
      this.fieldManagement.fieldModal.supportsPii ??
      false
    );
  }

  /** The picked type's settings section. Editing a field of a type the source
   *  can't create finds no entry for it, so it falls back to the first section
   *  the source declares — that field still has settings to show. */
  get settingsSection() {
    if (!this.form.$.type.value) {
      return undefined;
    }
    return (
      this.selectedFieldType?.settingsSection ??
      this.fieldManagement.fieldModal.types.find((t) => t.settingsSection)
        ?.settingsSection
    );
  }

  /** Not a computed: the warning may read data the source loaded outside MobX,
   *  so it's called afresh on every render. Same fallback as `settingsSection`. */
  get piiWarning() {
    const warning =
      this.selectedFieldType?.piiWarning ??
      this.fieldManagement.fieldModal.types.find((t) => t.piiWarning)
        ?.piiWarning;
    return warning?.();
  }

  get showSettings() {
    return (
      Boolean(this.fieldManagement.fieldModal.permissions) ||
      Boolean(this.settingsSection)
    );
  }

  get settingsHasError() {
    return Boolean(this.settingsSection?.hasError(this.form.$.type.value));
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const combineFieldSettings = (
  sectionSettings: FieldSettings | undefined,
  collectionSettings: FieldSettings | undefined,
): FieldSettings | undefined => {
  if (sectionSettings === undefined) {
    return collectionSettings;
  }

  if (collectionSettings === undefined) {
    return sectionSettings;
  }

  if (isRecord(sectionSettings) && isRecord(collectionSettings)) {
    return { ...sectionSettings, ...collectionSettings };
  }

  return collectionSettings;
};
